import React, { useCallback } from 'react';
import assistantClient from '@requests/telnyx/assistants.requests';

import { useParams, useNavigate } from 'react-router-dom';
import { LoadingButton } from '@common/Button';
import { FileUploadOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useDropzone } from 'react-dropzone';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface Props {
  disabled: boolean;
}

export const AssistantImport: React.FC<Props> = ({ disabled }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { service_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: save, isPending: isImporting } = useMutation({
    mutationFn: assistantClient.importAssistant,
    onSuccess: (data) => {
      if (!data) return;

      enqueueSnackbar(`Assistant ${data.name} imported successfully!`, { variant: 'success', autoHideDuration: 3000 });

      for (const tool of data.tools) {
        enqueueSnackbar(`Tool ${tool.name} assigned to assistant successfully!`, {
          variant: 'success',
          autoHideDuration: 3000,
        });
      }

      queryClient.invalidateQueries({ queryKey: ['ai_assistants'] });
      queryClient.invalidateQueries({ queryKey: ['ai_assistants_tools'] });

      navigate(`/${service_id}/assistants/${data.id}`);
    },
    onError: (error) => {
      enqueueSnackbar(error?.message, {
        variant: 'error',
        autoHideDuration: 10000,
      });

      enqueueSnackbar('Failed to import assistant!', {
        variant: 'error',
        autoHideDuration: 10000,
      });
    },
  });

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const reader = new FileReader();

    reader.onload = async (event) => {
      try {
        const input = event?.target?.result;

        if (typeof input === 'string') {
          const assistantImport = JSON.parse(input);

          try {
            await save({ assistant: assistantImport });
          } catch {}
        }
      } catch (error) {
        console.error('Error reading JSON:', error);

        if (error instanceof Error) {
          enqueueSnackbar(`Error Message: ${error?.message}`, {
            variant: 'error',
            autoHideDuration: 10000,
          });
        } else {
          enqueueSnackbar('Error Message: There was an unexpected configuration issue with the input file.', {
            variant: 'error',
            autoHideDuration: 10000,
          });
        }

        enqueueSnackbar('Failed to import assistant!', {
          variant: 'error',
          autoHideDuration: 10000,
        });
      }
    };

    reader.readAsText(acceptedFiles[0]);
  }, []);

  const { getInputProps, open } = useDropzone({
    onDrop: onDrop,
    noClick: true,
    noKeyboard: true,
    multiple: false,
    accept: { 'application/json': ['.json'] },
  });

  return (
    <div>
      <input {...getInputProps()} />
      <LoadingButton
        onClick={open}
        startIcon={<FileUploadOutlined sx={{ fontSize: '2.4ch !important' }} />}
        disabled={disabled}
        loading={isImporting}
        minWidth='100px'
        minHeight='32px'
      >
        Import
      </LoadingButton>
    </div>
  );
};
